import { db, checkFirebaseConnection, safeFirestoreWrite, safeFirestoreRead } from '../../shared/api/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// 컬렉션 이름을 결정하는 함수 (TeacherTest와 동일한 패턴)
const getCollectionName = (currentUser) => {
  return currentUser ? 'shortForm_Quiz_member' : 'shortForm_Quiz_guest';
};

// 사용자 ID 생성 함수 (TeacherTest와 동일한 패턴)
const generateUserId = (currentUser) => {
  if (currentUser) return currentUser.uid;
  
  const guestIdKey = 'guest_shortform_id';
  const existingGuestId = localStorage.getItem(guestIdKey);
  if (existingGuestId) return existingGuestId;

  const timestamp = new Date().getTime();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const newGuestId = `guest_${timestamp}_${randomStr}`;
  
  localStorage.setItem(guestIdKey, newGuestId);
  return newGuestId;
};

// localStorage 저장 키 생성 함수 (classId 지원)
const getStorageKey = (currentUser, classId = null) => {
  const baseKey = currentUser ? `shortForm_savedQuizzes_${currentUser.uid}` : 'shortForm_savedQuizzes_guest';
  return classId ? `${baseKey}_${classId}` : baseKey;
};

/**
 * 숏폼 퀴즈 저장 (TeacherTest 방식으로 변경)
 * - 선생님 문서 하나에 퀴즈 리스트로 관리
 * - LocalStorage 백업 저장 포함
 * - Firestore 저장(회원/비회원 분기) 포함
 */
export const saveShortFormQuiz = async ({ title, grade, subject, school, mode, topic, questions, currentUser, classId = null }) => {
  const nowIso = new Date().toISOString();
  const userId = generateUserId(currentUser);
  
  if (!userId) {
    return { success: false, error: '사용자 ID를 생성할 수 없습니다.' };
  }

  // Firestore 문서 ID 결정 (비회원+classId는 classId를 docId로 사용)
  const docId = classId ? (currentUser ? `${userId}_${classId}` : classId) : userId;
  // 문서 내에서 고유한 퀴즈 ID (타임스탬프)
  const uniqueQuizIdPart = `${Date.now()}`;
  // 최종적으로 사용될 ID (URL code, 식별자 등)
  const quizId = `${docId}::${uniqueQuizIdPart}`;

  // 퀴즈 데이터 객체 생성
  const newQuizData = {
    id: quizId, // 새로운 복합 ID 사용
    accessCode: quizId, // accessCode도 동일하게 설정
    title: title || '제목 없는 퀴즈',
    grade,
    subject,
    school,
    mode,
    topic,
    questions: questions || [],
    meta: { // meta 객체로 감싸서 QuizCard와 일치시킴
      grade,
      subject,
      school,
      mode,
      topic,
      classId: classId, // 퀴즈 자체에 classId를 기록
      ownerUid: currentUser?.uid || null // 퀴즈 생성자의 UID 저장
    },
    createdAt: nowIso,
    updatedAt: nowIso
  };

  // 1. LocalStorage 저장 (백업 및 오프라인 지원)
  try {
    const storageKey = getStorageKey(currentUser, classId);
    const savedQuizzes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    const updatedQuizzes = Array.isArray(savedQuizzes) ? [...savedQuizzes, newQuizData] : [newQuizData];
    localStorage.setItem(storageKey, JSON.stringify(updatedQuizzes));
  } catch (error) {
    console.warn('LocalStorage 저장 실패:', error);
  }

  // 2. Firestore 저장 시도
  let savedToFirestore = false;
  try {
    const connection = await checkFirebaseConnection();
    if (connection && connection.overall && db) {
      const collectionName = getCollectionName(currentUser);
      
      // 학급이 선택된 경우 학급별 문서 ID 생성
      let docId;
      if (classId) {
        docId = `${userId}_${classId}`;
      } else {
        docId = userId;
      }
      
      const userDocRef = doc(db, collectionName, docId);
      
      // 기존 문서 가져오기 (다른 데이터 보존을 위해)
      const readResult = await safeFirestoreRead(userDocRef);
      const existingData = readResult.success && readResult.doc.exists() ? readResult.doc.data() : {};
      
      // 기존 퀴즈 리스트와 병합
      const existingQuizzes = Array.isArray(existingData.퀴즈List) ? existingData.퀴즈List : [];
      const updatedQuizzes = [...existingQuizzes, newQuizData];
      
      // 문서 업데이트
      const dataToSave = {
        퀴즈List: updatedQuizzes,
        classId: classId || null,
        updatedAt: nowIso
      };
      
      const writeResult = await safeFirestoreWrite(userDocRef, dataToSave);
      savedToFirestore = !!writeResult.success;
    }
  } catch (error) {
    console.warn('Firestore 저장 실패:', error);
    savedToFirestore = false;
  }

  return {
    success: true,
    id: quizId,
    storage: { local: true, firestore: savedToFirestore }
  };
};

// AI로 숏폼 퀴즈 생성 (Firebase Functions -> GPT)
export const generateShortFormQuizAI = async ({ 
  school, 
  grade, 
  subject, 
  topic, 
  numQuestions = 5, 
  curriculum = '대한민국 국가교육과정',
  educationStandard = '2022 개정 교육과정'
}) => {
  try {
    const functions = getFunctions(getApp(), 'us-central1');
    const generateFn = httpsCallable(functions, 'generateShortFormQuiz');
    const res = await generateFn({ 
      school, 
      grade, 
      subject, 
      topic, 
      numQuestions,
      curriculum,
      educationStandard
    });
    return res.data;
  } catch (e) {
    return { success: false, questions: [] };
  }
};

/**
 * 숏폼 퀴즈 업데이트 (TeacherTest 방식으로 변경)
 * - 선생님 문서 내 퀴즈 리스트에서 특정 퀴즈 업데이트
 * - LocalStorage 갱신 포함
 * - Firestore 갱신(회원/비회원 분기) 포함
 */
export const updateShortFormQuiz = async ({ id, title, questions, meta, currentUser, classId = null }) => {
  const nowIso = new Date().toISOString();
  const userId = generateUserId(currentUser);

  if (!userId) {
    return { success: false, error: '사용자 ID를 생성할 수 없습니다.' };
  }

  // 1. LocalStorage 갱신
  try {
    const storageKey = getStorageKey(currentUser, classId);
    const savedQuizzes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    if (Array.isArray(savedQuizzes)) {
      const quizIndex = savedQuizzes.findIndex(quiz => quiz.id === id);
      if (quizIndex !== -1) {
        const currentQuiz = savedQuizzes[quizIndex];
        const updatedGrade = meta?.grade ?? currentQuiz.grade;
        const updatedSubject = meta?.subject ?? currentQuiz.subject;
        const updatedSchool = meta?.school ?? currentQuiz.school;
        const updatedMode = meta?.mode ?? currentQuiz.mode;
        const updatedTopic = meta?.topic ?? currentQuiz.topic;
        
        savedQuizzes[quizIndex] = {
          ...currentQuiz,
          title: title ?? currentQuiz.title,
          questions: questions || currentQuiz.questions,
          grade: updatedGrade,
          subject: updatedSubject,
          school: updatedSchool,
          mode: updatedMode,
          topic: updatedTopic,
          meta: { // meta 객체도 업데이트
            grade: updatedGrade,
            subject: updatedSubject,
            school: updatedSchool,
            mode: updatedMode,
            topic: updatedTopic,
            classId: meta?.classId ?? currentQuiz.meta?.classId,
            ownerUid: meta?.ownerUid ?? currentQuiz.meta?.ownerUid ?? (currentUser?.uid || null)
          },
          updatedAt: nowIso
        };
        localStorage.setItem(storageKey, JSON.stringify(savedQuizzes));
      }
    }
  } catch (error) {
    console.warn('LocalStorage 갱신 실패:', error);
  }

  // 2. Firestore 갱신 시도
  let updatedFirestore = false;
  try {
    const connection = await checkFirebaseConnection();
    if (connection && connection.overall && db) {
      const collectionName = getCollectionName(currentUser);
      
      // 학급이 선택된 경우 학급별 문서 ID 생성
      let docId;
      if (classId) {
        docId = `${userId}_${classId}`;
      } else {
        docId = userId;
      }
      
      const userDocRef = doc(db, collectionName, docId);
      
      // 기존 문서 가져오기
      const readResult = await safeFirestoreRead(userDocRef);
      if (readResult.success && readResult.doc.exists()) {
        const existingData = readResult.doc.data();
        const existingQuizzes = Array.isArray(existingData.퀴즈List) ? existingData.퀴즈List : [];
        
        // 특정 퀴즈 찾아서 업데이트
        const quizIndex = existingQuizzes.findIndex(quiz => quiz.id === id);
        if (quizIndex !== -1) {
          const currentQuiz = existingQuizzes[quizIndex];
          const updatedGrade = meta?.grade ?? currentQuiz.grade;
          const updatedSubject = meta?.subject ?? currentQuiz.subject;
          const updatedSchool = meta?.school ?? currentQuiz.school;
          const updatedMode = meta?.mode ?? currentQuiz.mode;
          const updatedTopic = meta?.topic ?? currentQuiz.topic;
          
          existingQuizzes[quizIndex] = {
            ...currentQuiz,
            title: title ?? currentQuiz.title,
            questions: questions || currentQuiz.questions,
            grade: updatedGrade,
            subject: updatedSubject,
            school: updatedSchool,
            mode: updatedMode,
            topic: updatedTopic,
            meta: { // meta 객체도 업데이트
              grade: updatedGrade,
              subject: updatedSubject,
              school: updatedSchool,
              mode: updatedMode,
              topic: updatedTopic,
              classId: meta?.classId ?? currentQuiz.meta?.classId,
              ownerUid: meta?.ownerUid ?? currentQuiz.meta?.ownerUid ?? (currentUser?.uid || null)
            },
            updatedAt: nowIso
          };
          
          // 문서 업데이트
          const dataToSave = {
            ...existingData,
            퀴즈List: existingQuizzes,
            updatedAt: nowIso
          };
          
          const writeResult = await safeFirestoreWrite(userDocRef, dataToSave);
          updatedFirestore = !!writeResult.success;
        }
      }
    }
  } catch (error) {
    console.warn('Firestore 갱신 실패:', error);
    updatedFirestore = false;
  }

  return { success: true, storage: { local: true, firestore: updatedFirestore } };
};

/**
 * 숏폼 퀴즈 삭제 (TeacherTest 방식으로 변경)
 * - 선생님 문서 내 퀴즈 리스트에서 특정 퀴즈 삭제
 * - LocalStorage 갱신 포함
 * - Firestore 갱신(회원/비회원 분기) 포함
 */
export const deleteShortFormQuiz = async ({ id, currentUser, classId = null }) => {
  const nowIso = new Date().toISOString();
  const userId = generateUserId(currentUser);

  if (!userId) {
    return { success: false, error: '사용자 ID를 생성할 수 없습니다.' };
  }

  let deletedFromFirestore = false;

  // 1. LocalStorage 갱신 (항상 실행)
  try {
    const storageKey = getStorageKey(currentUser, classId);
    const savedQuizzes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    if (Array.isArray(savedQuizzes)) {
      const filteredQuizzes = savedQuizzes.filter(quiz => quiz.id !== id);
      if (filteredQuizzes.length < savedQuizzes.length) {
        localStorage.setItem(storageKey, JSON.stringify(filteredQuizzes));
      }
    }
  } catch (error) {
    console.warn('LocalStorage 갱신 실패:', error);
  }

  // 비회원이고 classId가 없는 경우, 개인 localStorage에서도 삭제 시도
  if (!currentUser && !classId) {
     try {
      const storageKey = getStorageKey(null, null); // guest_shortform_savedQuizzes_guest
      const savedQuizzes = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(savedQuizzes)) {
        const filteredQuizzes = savedQuizzes.filter(quiz => quiz.id !== id);
        if (filteredQuizzes.length < savedQuizzes.length) {
          localStorage.setItem(storageKey, JSON.stringify(filteredQuizzes));
        }
      }
    } catch (error) {
      console.warn('Guest 개인 LocalStorage 갱신 실패:', error);
    }
  }

  // 2. Firestore 갱신 시도
  try {
    const connection = await checkFirebaseConnection();
    if (connection && connection.overall && db) {
      const collectionName = getCollectionName(currentUser);
      
      const docId = classId ? `${userId}_${classId}` : userId;
      const userDocRef = doc(db, collectionName, docId);
      
      const readResult = await safeFirestoreRead(userDocRef);
      if (readResult.success && readResult.doc.exists()) {
        const existingData = readResult.doc.data();
        const existingQuizzes = Array.isArray(existingData.퀴즈List) ? existingData.퀴즈List : [];
        
        if (existingQuizzes.some(quiz => quiz.id === id)) {
          const filteredQuizzes = existingQuizzes.filter(quiz => quiz.id !== id);
          
          const dataToSave = { ...existingData, 퀴즈List: filteredQuizzes, updatedAt: nowIso };
          const writeResult = await safeFirestoreWrite(userDocRef, dataToSave);
          deletedFromFirestore = !!writeResult.success;
        } else {
          // 해당 문서에 퀴즈가 없으면 성공으로 간주 (이미 삭제되었거나 다른 위치)
          deletedFromFirestore = true; 
        }
      } else {
        // 문서 자체가 없어도 삭제된 것으로 간주
        deletedFromFirestore = true;
      }
    }
  } catch (error) {
    console.warn('Firestore 갱신 실패:', error);
    deletedFromFirestore = false;
  }

  return { success: true, storage: { local: true, firestore: deletedFromFirestore } };
};

export default {
  saveShortFormQuiz,
  generateShortFormQuizAI,
  updateShortFormQuiz,
  deleteShortFormQuiz
};

 
